'use client';

import type { MouseEvent } from 'react';

import { TrackedAnchor } from '@/components/ancher';
import { TrackedButton } from '@/components/button';
import { TRACKED_EVENT_NAME, TRACKED_EVENT_POSITION } from '@/constants';
import { useAddClickButtonGtag } from '@/hooks/useGtag.hook';

// 카드 뒷면(검사 장점). 크기·간격·타이포는 반응형(모바일 280×448 → 데스크탑 360×488).
// 앞면 복귀 아이콘 버튼은 onFlipBack 이 전달됐을 때(모바일)만 노출된다 — 데스크탑은 hover-out 으로 복귀.
export function CardBack({
  name,
  benefits,
  learnMore,
  flipBackLabel,
  contentTabIndex = 0,
  href,
  trackSlug,
  onFlipBack,
}: {
  name: string;
  benefits: string[];
  learnMore: string;
  // 모바일 뒷면 우상단 아이콘 버튼(앞면 복귀)의 접근성 라벨
  flipBackLabel?: string;
  // 뒷면이 안 보일 때(-1) 내부 버튼/링크를 탭 순서에서 제외
  contentTabIndex?: number;
  href: string;
  trackSlug: string;
  // 모바일: 뒷면 빈 배경 탭 시 앞면으로. 데스크탑은 hover-out 으로 처리하므로 미전달.
  onFlipBack?: () => void;
}) {
  // 앞면(질문)으로 복귀 추적 — 아이콘/배경 탭 모두 같은 이벤트·라벨, method 로만 경로 구분
  const showQuestionLabel = `solution-finder_${trackSlug}_show-question`;
  const trackShowQuestion = useAddClickButtonGtag({
    label: showQuestionLabel,
    position: TRACKED_EVENT_POSITION.MAIN_CONTENT,
  });

  // 뒷면 빈 배경 탭 시에만 앞면으로(자식 CTA 클릭은 통과시키지 않음)
  const tapToFlipBack = onFlipBack
    ? (e: MouseEvent) => {
        if (e.target === e.currentTarget) {
          trackShowQuestion(undefined, { method: 'background' });
          onFlipBack();
        }
      }
    : undefined;

  return (
    <div
      onClick={tapToFlipBack}
      className="flex h-full w-full flex-col gap-3.5 rounded-[20px] bg-[#040436] px-6 pb-7 pt-[26px] md:gap-5 md:rounded-[32px] md:px-8 md:pb-8 md:pt-[29px]"
    >
      <div className="flex items-center justify-between">
        <p className="text-xl font-semibold leading-7 tracking-[-0.336px] text-white md:text-2xl md:leading-8 md:tracking-[-0.464px]">
          {name}
        </p>
        {onFlipBack && (
          <TrackedButton
            type="button"
            aria-label={flipBackLabel}
            label={showQuestionLabel}
            position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
            eventName={TRACKED_EVENT_NAME.CLICK_BUTTON}
            gtagOptions={{ method: 'icon' }}
            onClick={onFlipBack}
            tabIndex={contentTabIndex}
            className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[#F0F2F5] text-[#040436]"
          >
            {/* autorenew 아이콘(Figma 원본 글리프) */}
            <svg
              viewBox="0 0 11.4286 14.7068"
              className="size-[17px] rotate-90"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M5.71429 3.06758V4.34616C5.71429 4.66758 6.1 4.82473 6.32143 4.59616L8.31429 2.6033C8.45714 2.46044 8.45714 2.23901 8.31429 2.09616L6.32143 0.103299C6.1 -0.11813 5.71429 0.0390132 5.71429 0.360442V1.63901C2.55714 1.63901 0 4.19616 0 7.3533C0 8.09616 0.142857 8.81044 0.407143 9.46044C0.6 9.93901 1.21429 10.0676 1.57857 9.7033C1.77143 9.51044 1.85 9.21758 1.74286 8.96044C1.53571 8.46758 1.42857 7.91758 1.42857 7.3533C1.42857 4.98901 3.35 3.06758 5.71429 3.06758ZM9.85 5.0033C9.65714 5.19616 9.57857 5.49616 9.68571 5.74616C9.88571 6.24616 10 6.78901 10 7.3533C10 9.71758 8.07857 11.639 5.71429 11.639V10.3604C5.71429 10.039 5.32857 9.88187 5.10714 10.1104L3.11429 12.1033C2.97143 12.2462 2.97143 12.4676 3.11429 12.6104L5.10714 14.6033C5.32857 14.8247 5.71429 14.6676 5.71429 14.3533V13.0676C8.87143 13.0676 11.4286 10.5104 11.4286 7.3533C11.4286 6.61044 11.2857 5.89616 11.0214 5.24616C10.8286 4.76758 10.2143 4.63901 9.85 5.0033Z" />
            </svg>
          </TrackedButton>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-2 md:gap-2.5">
        {benefits.map(benefit => (
          <div
            key={benefit}
            className="flex flex-1 items-center justify-center rounded-[10px] bg-[rgba(238,238,245,0.19)] py-1.5 text-center md:px-3"
          >
            <p className="text-sm font-semibold leading-[22px] tracking-[-0.128px] text-white md:text-base md:leading-[26px] md:tracking-[-0.176px]">
              {benefit}
            </p>
          </div>
        ))}
      </div>
      <TrackedAnchor
        href={href}
        label={`solution-finder_${trackSlug}_learn-more`}
        position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
        target="_blank"
        rel="noopener"
        onClick={e => e.stopPropagation()}
        tabIndex={contentTabIndex}
        className="inline-flex items-center justify-center gap-1 rounded-[40px] border border-solid border-[#F0F2F5] py-2.5 text-sm font-semibold leading-[22px] tracking-[-0.14px] text-[#F0F2F5] transition-colors hover:bg-white/10 md:py-3 md:text-base md:leading-6 md:tracking-[-0.18px]"
      >
        {learnMore}
        <span aria-hidden="true">→</span>
      </TrackedAnchor>
    </div>
  );
}
